import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useBrandDesignStore } from '@/stores/brandDesign';
import type {
  BrandDesignStatusResponse,
  BrandDesignExtractionResponse,
  BrandDesignPhaseLog,
} from '@/services/api';
import { clsx } from 'clsx';
import {
  Paintbrush,
  Play,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Clock,
  ArrowLeft,
  Eye,
  Type,
  Palette,
  Layers,
  Grid3x3,
  Image,
  Box,
  RefreshCw,
} from 'lucide-react';

const PHASE_ORDER = ['extraction', 'analysis', 'synthesis', 'asset_generation', 'persistence'] as const;

const PHASE_LABELS: Record<string, string> = {
  extraction: 'Storefront Extraction',
  analysis: 'Style Analysis',
  synthesis: 'LLM Synthesis',
  asset_generation: 'Reference Images',
  persistence: 'Persistence',
};

export function BrandDesignPage() {
  const { clientUid } = useParams<{ clientUid?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { loading, error, clearError } = useBrandDesignStore();

  // Derive view from URL path
  type ViewKind = 'dashboard' | 'extraction' | 'tokens';
  const getViewKind = (): ViewKind => {
    if (location.pathname.endsWith('/extract')) return 'extraction';
    if (clientUid) return 'tokens';
    return 'dashboard';
  };

  const viewKind = getViewKind();

  const handleBack = () => navigate('/brand-design');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy-900 flex items-center gap-2">
            <Paintbrush size={22} className="text-brand-purple" />
            Brand Design System
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Extract, analyze, and manage brand design tokens from storefronts.
          </p>
        </div>
        {viewKind !== 'dashboard' && (
          <button
            onClick={handleBack}
            className="flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-navy-900 transition-colors"
          >
            <ArrowLeft size={14} />
            Back
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
          <button onClick={clearError} className="text-xs text-red-500 hover:text-red-700 font-medium">
            Dismiss
          </button>
        </div>
      )}

      {viewKind === 'dashboard' && (
        <DashboardView
          onStartExtraction={() => navigate('/brand-design/extract')}
          onViewTokens={(uid) => navigate(`/brand-design/${uid}`)}
        />
      )}
      {viewKind === 'extraction' && <ExtractionView />}
      {viewKind === 'tokens' && clientUid && <TokensView clientUid={clientUid} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dashboard View
// ---------------------------------------------------------------------------

function DashboardView({
  onStartExtraction,
  onViewTokens,
}: {
  onStartExtraction: () => void;
  onViewTokens: (clientUid: string) => void;
}) {
  const { clients, clientsLoading, fetchClients } = useBrandDesignStore();
  const [lookupUid, setLookupUid] = useState('');

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  return (
    <div className="space-y-6">
      {/* Action cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* New extraction card */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h2 className="font-semibold text-navy-900 text-sm mb-2">New Extraction</h2>
          <p className="text-xs text-slate-500 mb-4">
            Run the full brand design pipeline: Playwright extraction, style analysis,
            LLM synthesis, and token persistence.
          </p>
          <button
            onClick={onStartExtraction}
            className="flex items-center gap-2 px-4 py-2 bg-brand-purple text-white text-sm font-semibold rounded-lg hover:bg-brand-purple/90 transition-colors"
          >
            <Play size={14} />
            Start Extraction
          </button>
        </div>

        {/* Token lookup card */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h2 className="font-semibold text-navy-900 text-sm mb-2">Token Viewer</h2>
          <p className="text-xs text-slate-500 mb-4">
            View previously extracted brand design tokens by client UID.
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="e.g. skoutorganic"
              value={lookupUid}
              onChange={(e) => setLookupUid(e.target.value)}
              className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-brand-teal focus:ring-1 focus:ring-brand-teal/20"
            />
            <button
              disabled={!lookupUid.trim()}
              onClick={() => onViewTokens(lookupUid.trim())}
              className="flex items-center gap-1.5 px-4 py-2 bg-navy-900 text-brand-teal text-sm font-semibold rounded-lg hover:bg-navy-800 transition-colors disabled:opacity-40"
            >
              <Eye size={14} />
              View
            </button>
          </div>
        </div>
      </div>

      {/* Configured Brands List */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-navy-900 text-sm">Configured Brands</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Brands with extracted design tokens
            </p>
          </div>
          <button
            onClick={fetchClients}
            disabled={clientsLoading}
            className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-navy-900 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={12} className={clientsLoading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
        <div className="p-5">
          {clientsLoading ? (
            <div className="flex items-center justify-center py-8 gap-2 text-slate-400 text-sm">
              <Loader2 size={16} className="animate-spin" />
              Loading brands...
            </div>
          ) : clients.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">
              No brands configured yet. Run an extraction to add a brand.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {clients.map((client) => (
                <button
                  key={client.client_id}
                  onClick={() => onViewTokens(client.client_id)}
                  className="flex items-center gap-3 p-4 bg-slate-50 hover:bg-slate-100 rounded-lg border border-slate-100 hover:border-slate-200 transition-all text-left group"
                >
                  <div className="w-10 h-10 rounded-lg bg-brand-purple/10 flex items-center justify-center flex-shrink-0">
                    <Paintbrush size={18} className="text-brand-purple" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-navy-900 text-sm truncate">
                      {client.brand_name || client.client_id}
                    </div>
                    <div className="text-[10px] text-slate-400 font-mono truncate">
                      {client.client_id}
                    </div>
                  </div>
                  {client.version && (
                    <span className="text-[10px] font-mono bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded flex-shrink-0">
                      v{client.version}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Pipeline overview */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <h2 className="font-semibold text-navy-900 text-sm mb-4">Pipeline Stages</h2>
        <div className="grid grid-cols-5 gap-3">
          {PHASE_ORDER.map((phase, i) => (
            <div
              key={phase}
              className="bg-slate-50 rounded-lg border border-slate-100 p-4 text-center"
            >
              <div className="text-[10px] font-mono text-slate-400 uppercase mb-1">
                Step {i + 1}
              </div>
              <div className="text-sm font-semibold text-navy-900">
                {PHASE_LABELS[phase]}
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                {phase === 'extraction' && 'Playwright screenshots, CSS, meta'}
                {phase === 'analysis' && 'Colors, typography, spacing'}
                {phase === 'synthesis' && 'GPT-4o / Gemini token generation'}
                {phase === 'asset_generation' && 'AI lifestyle images (optional)'}
                {phase === 'persistence' && 'ClientDataStore + GCS backup'}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Extraction View
// ---------------------------------------------------------------------------

function ExtractionView() {
  const { submitAsyncExtraction, pollStatus, pollingStatus, polling, loading, lastExtraction } =
    useBrandDesignStore();

  const [form, setForm] = useState({
    client_uid: '',
    storefront_url: '',
    client_name: '',
    client_industry: '',
    synthesis_model: 'openai',
    generate_reference_images: false,
  });

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (polling && form.client_uid) {
      intervalRef.current = setInterval(() => {
        pollStatus(form.client_uid);
      }, 3000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [polling, form.client_uid, pollStatus]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.client_uid || !form.storefront_url) return;
    await submitAsyncExtraction({
      client_uid: form.client_uid,
      storefront_url: form.storefront_url,
      client_name: form.client_name || undefined,
      client_industry: form.client_industry || undefined,
      synthesis_model: form.synthesis_model,
      generate_reference_images: form.generate_reference_images,
      persist: true,
    });
    pollStatus(form.client_uid);
  };

  const canSubmit = form.client_uid.trim() && form.storefront_url.trim() && !loading && !polling;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Form */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <h2 className="font-semibold text-navy-900 text-sm mb-4">Extraction Parameters</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Field label="Client UID *" hint="Unique identifier, e.g. 'skoutorganic'">
            <input
              type="text"
              required
              value={form.client_uid}
              onChange={(e) => setForm((f) => ({ ...f, client_uid: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-brand-teal focus:ring-1 focus:ring-brand-teal/20"
              placeholder="skoutorganic"
            />
          </Field>
          <Field label="Storefront URL *" hint="Public URL of the storefront to analyze">
            <input
              type="url"
              required
              value={form.storefront_url}
              onChange={(e) => setForm((f) => ({ ...f, storefront_url: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-brand-teal focus:ring-1 focus:ring-brand-teal/20"
              placeholder="https://skoutorganic.com"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Brand Name" hint="Optional">
              <input
                type="text"
                value={form.client_name}
                onChange={(e) => setForm((f) => ({ ...f, client_name: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-brand-teal focus:ring-1 focus:ring-brand-teal/20"
                placeholder="Skout Organic"
              />
            </Field>
            <Field label="Industry" hint="Optional">
              <input
                type="text"
                value={form.client_industry}
                onChange={(e) => setForm((f) => ({ ...f, client_industry: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-brand-teal focus:ring-1 focus:ring-brand-teal/20"
                placeholder="CPG"
              />
            </Field>
          </div>
          <Field label="Synthesis Model">
            <select
              value={form.synthesis_model}
              onChange={(e) => setForm((f) => ({ ...f, synthesis_model: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-brand-teal bg-white"
            >
              <option value="openai">OpenAI (GPT-4o)</option>
              <option value="gemini">Google (Gemini)</option>
            </select>
          </Field>

          {/* Reference Images Toggle */}
          <label className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100 cursor-pointer hover:border-slate-200 transition-colors">
            <input
              type="checkbox"
              checked={form.generate_reference_images}
              onChange={(e) => setForm((f) => ({ ...f, generate_reference_images: e.target.checked }))}
              className="mt-0.5 w-4 h-4 rounded border-slate-300 text-brand-purple focus:ring-brand-purple/20"
            />
            <div>
              <span className="text-xs font-semibold text-navy-900">Generate Reference Images</span>
              <p className="text-[10px] text-slate-500 mt-0.5">
                Generate 2-3 AI lifestyle images based on brand imagery guidelines. Adds ~30-60s to extraction.
              </p>
            </div>
          </label>

          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-brand-purple text-white text-sm font-semibold rounded-lg hover:bg-brand-purple/90 transition-colors disabled:opacity-40"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />}
            {loading ? 'Submitting...' : 'Run Async Extraction'}
          </button>
        </form>
      </div>

      {/* Status / Progress panel */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <h2 className="font-semibold text-navy-900 text-sm mb-4">Extraction Progress</h2>
        {polling || pollingStatus ? (
          <ExtractionProgress status={pollingStatus} polling={polling} />
        ) : lastExtraction ? (
          <ExtractionResult result={lastExtraction} />
        ) : (
          <div className="text-center py-12 text-slate-400 text-sm">
            Submit an extraction to see progress here.
          </div>
        )}
      </div>
    </div>
  );
}

function ExtractionProgress({
  status,
  polling,
}: {
  status: BrandDesignStatusResponse | null;
  polling: boolean;
}) {
  if (!status) {
    return (
      <div className="flex items-center justify-center py-12 gap-2 text-slate-400 text-sm">
        <Loader2 size={16} className="animate-spin" />
        Waiting for initial status...
      </div>
    );
  }

  const progress = status.progress ?? 0;

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="font-medium text-navy-900">
            {status.status === 'completed'
              ? 'Complete'
              : status.status === 'failed'
                ? 'Failed'
                : 'In Progress'}
          </span>
          <span className="text-slate-400">{progress}%</span>
        </div>
        <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
          <div
            className={clsx(
              'h-full rounded-full transition-all duration-500',
              status.status === 'failed' ? 'bg-red-400' : 'bg-brand-teal',
            )}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="space-y-2">
        {PHASE_ORDER.map((phase) => {
          const phaseProgress = getPhaseStatus(phase, progress, status.status);
          return (
            <div
              key={phase}
              className={clsx(
                'flex items-center gap-3 px-3 py-2 rounded-lg border text-sm',
                phaseProgress === 'complete'
                  ? 'border-emerald-100 bg-emerald-50'
                  : phaseProgress === 'active'
                    ? 'border-brand-teal/30 bg-brand-teal/5'
                    : 'border-slate-100 bg-slate-50',
              )}
            >
              {phaseProgress === 'complete' ? (
                <CheckCircle2 size={14} className="text-emerald-500 flex-shrink-0" />
              ) : phaseProgress === 'active' ? (
                <Loader2 size={14} className="text-brand-teal animate-spin flex-shrink-0" />
              ) : (
                <Clock size={14} className="text-slate-300 flex-shrink-0" />
              )}
              <span
                className={clsx(
                  'font-medium',
                  phaseProgress === 'complete'
                    ? 'text-emerald-700'
                    : phaseProgress === 'active'
                      ? 'text-navy-900'
                      : 'text-slate-400',
                )}
              >
                {PHASE_LABELS[phase]}
              </span>
            </div>
          );
        })}
      </div>

      {status.token_summary && (
        <div className="bg-slate-50 rounded-lg border border-slate-100 p-3 text-xs">
          <div className="font-semibold text-navy-900 mb-1">Token Summary</div>
          {status.token_summary.brand_name && (
            <div className="text-slate-500">Brand: {status.token_summary.brand_name}</div>
          )}
          {status.token_summary.sections && (
            <div className="text-slate-500">
              Sections: {status.token_summary.sections.join(', ')}
            </div>
          )}
        </div>
      )}

      {status.error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg px-3 py-2">
          {status.error}
        </div>
      )}

      {polling && (
        <div className="text-[10px] text-slate-400 text-center font-mono">
          Polling every 3s...
        </div>
      )}
    </div>
  );
}

function ExtractionResult({ result }: { result: BrandDesignExtractionResponse | null }) {
  if (!result) return null;
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        {result.status === 'completed' ? (
          <CheckCircle2 size={16} className="text-emerald-500" />
        ) : (
          <AlertCircle size={16} className="text-red-400" />
        )}
        <span className="text-sm font-semibold text-navy-900 capitalize">{result.status}</span>
      </div>
      {result.phase_log.map((p: BrandDesignPhaseLog) => (
        <div key={p.phase} className="text-xs text-slate-500 flex items-center gap-2">
          <CheckCircle2 size={12} className="text-emerald-400" />
          {PHASE_LABELS[p.phase] ?? p.phase} — {p.status}
        </div>
      ))}
      {result.persisted && (
        <div className="text-xs text-emerald-600 font-medium">Tokens persisted successfully.</div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tokens View — Enhanced Design System Explorer
// ---------------------------------------------------------------------------

type TokenTab = 'overview' | 'colors' | 'typography' | 'components' | 'imagery' | 'spacing' | 'raw';

function TokensView({ clientUid }: { clientUid: string }) {
  const { fetchTokens, tokens, loading } = useBrandDesignStore();
  const [activeTab, setActiveTab] = useState<TokenTab>('overview');

  useEffect(() => {
    fetchTokens(clientUid);
  }, [clientUid, fetchTokens]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 gap-2 text-slate-400 text-sm">
        <Loader2 size={16} className="animate-spin" />
        Loading tokens for {clientUid}...
      </div>
    );
  }

  if (!tokens) {
    return (
      <div className="text-center py-16 text-slate-400 text-sm">
        No tokens found for &ldquo;{clientUid}&rdquo;.
      </div>
    );
  }

  const tabs: { id: TokenTab; label: string; icon: React.ElementType }[] = [
    { id: 'overview', label: 'Overview', icon: Eye },
    { id: 'colors', label: 'Colors', icon: Palette },
    { id: 'typography', label: 'Typography', icon: Type },
    { id: 'components', label: 'Components', icon: Box },
    { id: 'imagery', label: 'Imagery', icon: Image },
    { id: 'spacing', label: 'Spacing', icon: Grid3x3 },
    { id: 'raw', label: 'Raw JSON', icon: Layers },
  ];

  return (
    <div className="space-y-4">
      {/* Brand header */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-brand-purple/10 flex items-center justify-center">
            <Paintbrush size={24} className="text-brand-purple" />
          </div>
          <div className="flex-1">
            <h2 className="font-bold text-navy-900 text-lg">
              {(tokens.brand_name as string) ?? clientUid}
            </h2>
            <p className="text-xs text-slate-400 font-mono">{clientUid}</p>
          </div>
          <div className="flex flex-wrap gap-2 text-[10px] font-mono">
            {typeof tokens.version === 'string' && (
              <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                v{tokens.version}
              </span>
            )}
            <span className="bg-brand-purple/10 text-brand-purple px-2 py-0.5 rounded">
              {Object.keys(tokens).length} sections
            </span>
          </div>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={clsx(
              'flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-colors whitespace-nowrap',
              activeTab === tab.id
                ? 'bg-white text-navy-900 shadow-sm'
                : 'text-slate-500 hover:text-navy-900',
            )}
          >
            <tab.icon size={14} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        {activeTab === 'overview' && <OverviewTab tokens={tokens} />}
        {activeTab === 'colors' && <ColorsTab tokens={tokens} />}
        {activeTab === 'typography' && <TypographyTab tokens={tokens} />}
        {activeTab === 'components' && <ComponentsTab tokens={tokens} />}
        {activeTab === 'imagery' && <ImageryTab tokens={tokens} />}
        {activeTab === 'spacing' && <SpacingTab tokens={tokens} />}
        {activeTab === 'raw' && <RawJsonTab tokens={tokens} />}
      </div>
    </div>
  );
}

// Overview Tab
function OverviewTab({ tokens }: { tokens: Record<string, unknown> }) {
  const colors = (tokens.color ?? tokens.colors ?? {}) as Record<string, unknown>;
  const typography = (tokens.typography ?? {}) as Record<string, unknown>;
  const components = (tokens.components ?? {}) as Record<string, unknown>;
  const source = (tokens.source_observations ?? {}) as Record<string, unknown>;

  return (
    <div className="p-6 space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Color preview */}
        <div className="bg-slate-50 rounded-lg p-4">
          <h3 className="text-xs font-semibold text-slate-500 uppercase mb-3">Color Palette</h3>
          <div className="flex gap-1">
            {extractMainColors(colors).slice(0, 6).map((color, i) => (
              <div
                key={i}
                className="flex-1 h-8 rounded"
                style={{ backgroundColor: color }}
                title={color}
              />
            ))}
          </div>
        </div>

        {/* Typography preview */}
        <div className="bg-slate-50 rounded-lg p-4">
          <h3 className="text-xs font-semibold text-slate-500 uppercase mb-3">Typography</h3>
          {typography.font_families ? (
            <div className="space-y-1">
              {Object.entries(typography.font_families as Record<string, unknown>).slice(0, 2).map(([role, fam]) => (
                <div key={role} className="text-xs">
                  <span className="text-slate-400">{role}:</span>{' '}
                  <span className="text-navy-900 font-medium">
                    {typeof fam === 'object' && fam !== null ? (fam as Record<string, unknown>).family as string : String(fam)}
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        {/* Components preview */}
        <div className="bg-slate-50 rounded-lg p-4">
          <h3 className="text-xs font-semibold text-slate-500 uppercase mb-3">Components</h3>
          <div className="flex flex-wrap gap-1">
            {Object.keys(components).slice(0, 5).map((key) => (
              <span key={key} className="text-[10px] bg-brand-purple/10 text-brand-purple px-2 py-0.5 rounded">
                {key}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Source info */}
      {Object.keys(source).length > 0 && (
        <div className="border-t border-slate-100 pt-4">
          <h3 className="text-xs font-semibold text-slate-500 uppercase mb-3">Source</h3>
          <div className="grid grid-cols-2 gap-2 text-xs">
            {Object.entries(source).map(([key, val]) => (
              <div key={key}>
                <span className="text-slate-400">{key}:</span>{' '}
                <span className="text-navy-900">{typeof val === 'object' ? JSON.stringify(val) : String(val)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Colors Tab
function ColorsTab({ tokens }: { tokens: Record<string, unknown> }) {
  const colors = (tokens.color ?? tokens.colors ?? {}) as Record<string, unknown>;

  return (
    <div className="p-6 space-y-6">
      {Object.entries(colors).map(([paletteName, palette]) => {
        if (typeof palette !== 'object' || palette === null) return null;
        const shades = Object.entries(palette as Record<string, unknown>)
          .filter(([, v]) => typeof v === 'string' && (v.startsWith('#') || v.startsWith('rgb')))
          .sort((a, b) => parseInt(a[0]) - parseInt(b[0]));

        if (shades.length === 0) return null;

        return (
          <div key={paletteName}>
            <h3 className="text-sm font-semibold text-navy-900 capitalize mb-3">{paletteName}</h3>
            <div className="flex gap-2">
              {shades.map(([shade, hex]) => (
                <div key={shade} className="text-center flex-1 max-w-20">
                  <div
                    className="w-full h-12 rounded-lg border border-slate-200 shadow-sm cursor-pointer hover:scale-105 transition-transform"
                    style={{ backgroundColor: hex as string }}
                    title={`Click to copy ${hex}`}
                    onClick={() => navigator.clipboard.writeText(hex as string)}
                  />
                  <div className="text-[10px] text-slate-500 mt-1">{shade}</div>
                  <div className="text-[9px] font-mono text-slate-400">{hex as string}</div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Typography Tab
function TypographyTab({ tokens }: { tokens: Record<string, unknown> }) {
  const typography = (tokens.typography ?? {}) as Record<string, unknown>;
  const fontFamilies = typography.font_families as Record<string, unknown> | undefined;
  const scale = typography.scale as Record<string, unknown> | undefined;

  return (
    <div className="p-6 space-y-6">
      {fontFamilies && (
        <div>
          <h3 className="text-sm font-semibold text-navy-900 mb-3">Font Families</h3>
          <div className="space-y-3">
            {Object.entries(fontFamilies).map(([role, fam]) => {
              const family = typeof fam === 'object' && fam !== null ? (fam as Record<string, unknown>).family as string : String(fam);
              return (
                <div key={role} className="bg-slate-50 rounded-lg p-4">
                  <span className="text-[10px] bg-brand-purple/10 text-brand-purple px-2 py-0.5 rounded uppercase">{role}</span>
                  <div className="text-xl mt-2" style={{ fontFamily: `'${family}', sans-serif` }}>
                    {family}
                  </div>
                  <div className="text-xs text-slate-400 font-mono mt-1">{family}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {scale && (
        <div>
          <h3 className="text-sm font-semibold text-navy-900 mb-3">Type Scale</h3>
          <div className="space-y-2">
            {Object.entries(scale).map(([name, entry]) => {
              const e = entry as Record<string, unknown>;
              return (
                <div key={name} className="flex items-center gap-4 py-2 border-b border-slate-100 last:border-0">
                  <span className="text-xs text-slate-400 w-16">{name}</span>
                  <span style={{ fontSize: e.size as string || '1rem', fontWeight: e.weight as number || 400 }}>
                    The quick brown fox
                  </span>
                  <span className="text-[10px] font-mono text-slate-400 ml-auto">
                    {e.size as string} / w{e.weight as number}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// Components Tab
function ComponentsTab({ tokens }: { tokens: Record<string, unknown> }) {
  const components = (tokens.components ?? {}) as Record<string, unknown>;

  return (
    <div className="p-6 space-y-4">
      {Object.entries(components).map(([name, data]) => (
        <div key={name} className="bg-slate-50 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-navy-900 capitalize mb-3">{name}</h3>
          <KeyValueList data={data as Record<string, unknown>} />
        </div>
      ))}
    </div>
  );
}

// Imagery Tab
function ImageryTab({ tokens }: { tokens: Record<string, unknown> }) {
  const imagery = (tokens.imagery ?? {}) as Record<string, unknown>;
  const referenceImages = imagery.reference_images as Array<{
    type: string;
    url: string;
    prompt?: string;
    aspect_ratio?: string;
    generated_at?: string;
  }> | undefined;

  // Separate reference images from other imagery metadata
  const otherSections = Object.entries(imagery).filter(([key]) => key !== 'reference_images');

  return (
    <div className="p-6 space-y-6">
      {/* Reference Images Section */}
      {referenceImages && referenceImages.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-navy-900 mb-4">Reference Images</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {referenceImages.map((img, idx) => (
              <div
                key={idx}
                className="bg-slate-50 rounded-lg border border-slate-200 overflow-hidden group"
              >
                <div className="aspect-video bg-slate-200 relative">
                  <img
                    src={img.url}
                    alt={img.type}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // Hide broken images gracefully
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <div className="p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-navy-900 capitalize">
                      {img.type.replace(/_/g, ' ')}
                    </span>
                    {img.aspect_ratio && (
                      <span className="text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">
                        {img.aspect_ratio}
                      </span>
                    )}
                  </div>
                  {img.prompt && (
                    <p className="text-[10px] text-slate-500 line-clamp-2" title={img.prompt}>
                      {img.prompt}
                    </p>
                  )}
                  {img.generated_at && (
                    <div className="text-[9px] text-slate-400 mt-1 font-mono">
                      Generated: {new Date(img.generated_at).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Placeholder when no reference images */}
      {(!referenceImages || referenceImages.length === 0) && (
        <div className="bg-slate-50 rounded-lg border-2 border-dashed border-slate-200 p-8 text-center">
          <Image size={32} className="mx-auto text-slate-300 mb-3" />
          <h3 className="text-sm font-semibold text-slate-500 mb-1">No Reference Images</h3>
          <p className="text-xs text-slate-400 max-w-md mx-auto">
            Reference images can be generated during extraction by enabling the
            &quot;Generate Reference Images&quot; option. These provide visual
            examples for lifestyle shots and brand imagery.
          </p>
        </div>
      )}

      {/* Imagery Metadata Sections */}
      {otherSections.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-navy-900 mb-4">Imagery Guidelines</h3>
          <div className="space-y-4">
            {otherSections.map(([section, data]) => (
              <div key={section} className="bg-slate-50 rounded-lg p-4">
                <h4 className="text-xs font-semibold text-slate-600 uppercase mb-3">
                  {section.replace(/_/g, ' ')}
                </h4>
                <KeyValueList data={data as Record<string, unknown>} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Spacing Tab
function SpacingTab({ tokens }: { tokens: Record<string, unknown> }) {
  const spacing = (tokens.spacing ?? {}) as Record<string, unknown>;

  return (
    <div className="p-6 space-y-4">
      {Object.entries(spacing).map(([group, vals]) => (
        <div key={group} className="bg-slate-50 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-navy-900 capitalize mb-3">{group.replace(/_/g, ' ')}</h3>
          {typeof vals === 'object' && vals !== null ? (
            <KeyValueList data={vals as Record<string, unknown>} />
          ) : (
            <span className="text-sm text-slate-600">{String(vals)}</span>
          )}
        </div>
      ))}
    </div>
  );
}

// Raw JSON Tab
function RawJsonTab({ tokens }: { tokens: Record<string, unknown> }) {
  return (
    <div className="p-6">
      <pre className="bg-slate-900 text-slate-100 rounded-lg p-4 text-[11px] font-mono overflow-auto max-h-[600px] leading-relaxed">
        {JSON.stringify(tokens, null, 2)}
      </pre>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-navy-900">{label}</span>
      {hint && <span className="text-[10px] text-slate-400 ml-1">({hint})</span>}
      <div className="mt-1">{children}</div>
    </label>
  );
}

function KeyValueList({ data }: { data: Record<string, unknown> }) {
  const entries = Object.entries(data);
  if (entries.length === 0) return <p className="text-xs text-slate-400">No data</p>;

  return (
    <div className="space-y-1.5 max-h-48 overflow-auto">
      {entries.map(([k, v]) => (
        <div key={k} className="flex items-start justify-between text-xs border-b border-slate-100 pb-1 last:border-0">
          <span className="text-slate-600 font-medium">{k}</span>
          <span className="text-slate-400 font-mono text-right max-w-[60%] truncate">
            {typeof v === 'object' ? JSON.stringify(v) : String(v)}
          </span>
        </div>
      ))}
    </div>
  );
}

function extractMainColors(colors: Record<string, unknown>): string[] {
  const result: string[] = [];
  // Priority order for palette keys - pick representative colors
  const priorityKeys = ['primary', 'secondary', 'accent', 'brand', 'neutral'];
  // Preferred shade levels (most representative first)
  const preferredShades = ['500', '400', '600', '300', '700'];

  for (const key of priorityKeys) {
    const palette = colors[key];
    if (typeof palette !== 'object' || palette === null) continue;
    const paletteObj = palette as Record<string, unknown>;

    // Try to find a representative shade
    for (const shade of preferredShades) {
      if (typeof paletteObj[shade] === 'string' && (paletteObj[shade] as string).startsWith('#')) {
        result.push(paletteObj[shade] as string);
        break;
      }
    }
    // Fallback: if no shade found, try to get any hex color from the palette
    if (result.length === 0 || result[result.length - 1] !== paletteObj[preferredShades[0]]) {
      for (const [, val] of Object.entries(paletteObj)) {
        if (typeof val === 'string' && val.startsWith('#') && !result.includes(val)) {
          result.push(val);
          break;
        }
      }
    }
  }

  // Also add semantic colors if available
  const semantic = colors['semantic'] as Record<string, unknown> | undefined;
  if (semantic) {
    for (const key of ['success', 'warning', 'error', 'info']) {
      const val = semantic[key];
      if (typeof val === 'string' && val.startsWith('#') && !result.includes(val)) {
        result.push(val);
      }
    }
  }

  return result;
}

function getPhaseStatus(
  phase: string,
  progress: number,
  overallStatus: string | undefined,
): 'complete' | 'active' | 'pending' {
  // 5 phases: extraction (25), analysis (50), synthesis (75), asset_generation (85), persistence (100)
  const thresholds: Record<string, number> = {
    extraction: 25,
    analysis: 50,
    synthesis: 75,
    asset_generation: 85,
    persistence: 100,
  };

  const prevThresholds: Record<string, number> = {
    extraction: 0,
    analysis: 25,
    synthesis: 50,
    asset_generation: 75,
    persistence: 85,
  };

  const threshold = thresholds[phase] ?? 100;
  const prevThreshold = prevThresholds[phase] ?? 0;

  if (overallStatus === 'failed' && progress < threshold && progress >= prevThreshold) {
    return 'active';
  }
  if (progress >= threshold) return 'complete';
  if (progress >= prevThreshold) return 'active';
  return 'pending';
}
